// App.ts: Orchestrates simulation and UI, supports extensibility for research/education.
// Purpose: Modular entry point for simulation, visualization, and psychic potential comparison.
// Extensibility: Supports plugins, export/import, and custom research logic.

import { NeuralNetworkSimulator } from './NeuralNetworkSimulator';
import { UI } from './UI';
import { comparePsychicPotential } from './PsychicPotential';
import { ParameterRegistry } from './core/parameterRegistry';
import { PixelMapPanel } from './ui/PixelMapPanel';

export class App {
	network: NeuralNetworkSimulator & {
		runPlugins: () => void;
		hasFailed: () => boolean;
		startSimulation: () => void;
		simulateEvolution: () => void;
		cycle: () => any;
		registerPlugin: (plugin: (sim: NeuralNetworkSimulator) => void) => void;
		removeCorrodedNodes: () => void;
	};
	ui: UI;
	loopActive: boolean = true;
	private manuallyPaused: boolean = false;
	private paramRegistry: ParameterRegistry;
	private pixelPanel: PixelMapPanel;

	constructor() {
		this.network = new NeuralNetworkSimulator();
		// Add these lines to ensure optional methods exist
		this.network.removeCorrodedNodes = this.network.removeCorrodedNodes || (() => {});
		this.network.runPlugins = this.network.runPlugins || (() => {});
		this.network.hasFailed = this.network.hasFailed || (() => false);
		this.network.startSimulation = this.network.startSimulation || (() => {});
		this.network.simulateEvolution = this.network.simulateEvolution || (() => {});
		this.network.cycle = this.network.cycle || (() => ({}));
		this.network.registerPlugin = this.network.registerPlugin || (() => {});
		this.ui = new UI(this.network);

		// Parameter visualization setup
		this.paramRegistry = new ParameterRegistry();
		this.pixelPanel = new PixelMapPanel(260, 6);
		// Providers: simulator
		this.paramRegistry.registerProvider(() => {
			const arr: any[] = [];
			const nodeCount = this.network.nodes?.length || 0;
			const edgeCount = this.network.edges?.length || 0;
			const totalEnergy = this.network.nodes?.reduce((s: number, n: any) => s + (n.energy || 0), 0) || 0;
			arr.push({ id: 'sim.step', name: 'Step', category: 'simulation', type: 'numeric', value: this.network.step, min: 0 });
			arr.push({ id: 'sim.nodes', name: 'Nodes', category: 'simulation', type: 'numeric', value: nodeCount, min: 0 });
			arr.push({ id: 'sim.edges', name: 'Edges', category: 'simulation', type: 'numeric', value: edgeCount, min: 0 });
			arr.push({ id: 'sim.energy.total', name: 'Total Energy', category: 'energy', type: 'numeric', value: totalEnergy, min: 0 });
			arr.push({ id: 'sim.stability', name: 'Stability', category: 'dynamics', type: 'numeric', value: (this.network as any).stabilityMetric || 0, min: 0, max: 1 });
			return arr;
		});
		// Providers: breathing
		this.paramRegistry.registerProvider(() => {
			const b: any = (this.network as any).breathingSystem;
			if (!b || !b.state) return [];
			const s = b.state;
			return [
				{ id: 'breath.oxygen', name: 'Oxygen Level', category: 'breathing', type: 'numeric', value: s.oxygenLevel ?? 0, min: 0, max: 1 },
				{ id: 'breath.co2', name: 'CO2 Level', category: 'breathing', type: 'numeric', value: s.carbonDioxideLevel ?? 0, min: 0, max: 1 },
				{ id: 'breath.rate', name: 'Resp Rate', category: 'breathing', type: 'numeric', value: s.respiratoryRate ?? 0, min: 0 },
				{ id: 'breath.phase', name: 'Phase', category: 'breathing', type: 'categorical', value: String(s.cyclePhase ?? 'unknown') },
				{ id: 'breath.fatigue', name: 'Fatigue', category: 'breathing', type: 'numeric', value: s.fatigueLevel ?? 0, min: 0, max: 1 },
				{ id: 'breath.stress', name: 'Stress', category: 'breathing', type: 'numeric', value: s.stressLevel ?? 0, min: 0, max: 1 }
			];
		});
		// Providers: archetypes
		this.paramRegistry.registerProvider(() => {
			const engine: any = (this.network as any).archetypeEngine;
			if (!engine || typeof engine.getSummary !== 'function') return [];
			const sum = engine.getSummary();
			if (!sum || !Array.isArray(sum.all)) return [];
			return sum.all.map((a: any) => ({
				id: `arch.${a.key}`,
				name: a.name || a.key,
				category: 'archetype',
				type: 'numeric',
				value: a.score ?? 0,
				min: 0,
				max: 1
			}));
		});
		// Providers: voice characteristics
		this.paramRegistry.registerProvider(() => {
			const v = this.network.voiceCharacteristics as any;
			return [
				{ id: 'voice.pitch', name: 'Voice Pitch', category: 'voice', type: 'numeric', value: v.pitch ?? 0, min: 50, max: 500 },
				{ id: 'voice.breathiness', name: 'Breathiness', category: 'voice', type: 'numeric', value: v.breathiness ?? 0, min: 0, max: 1 },
				{ id: 'voice.roughness', name: 'Roughness', category: 'voice', type: 'numeric', value: v.roughness ?? 0, min: 0, max: 1 },
				{ id: 'voice.sustain', name: 'Sustain', category: 'voice', type: 'numeric', value: v.sustainLevel ?? 0, min: 0, max: 1 }
			];
		});
		// Providers: zodiac/element mix from nodes
		this.paramRegistry.registerProvider(() => {
			const arr: any[] = [];
			const nodes = this.network.nodes || [];
			if (nodes.length === 0) return arr;
			const counts: Record<string, number> = {};
			const elemCounts: Record<string, number> = {};
			const toZodiac = (hue: number): string => {
				if (hue >= 60 && hue < 80) return 'Taurus';
				else if (hue >= 80 && hue < 100) return 'Capricorn';
				else if (hue >= 100 && hue < 120) return 'Scorpio';
				else if (hue >= 120 && hue < 150) return 'Cancer';
				else if (hue >= 60 && hue < 150) return 'Virgo';
				else if (hue >= 150 && hue < 200) return 'Gemini';
				else if (hue >= 200 && hue < 260) return 'Aquarius';
				else if (hue >= 260 && hue < 320) return 'Pisces';
				else if (hue >= 320 && hue < 340) return 'Libra';
				else if (hue >= 40 && hue < 60) return 'Sagittarius';
				else if (hue >= 20 && hue < 40) return 'Leo';
				else return 'Aries';
			};
			const zodiacToElement: Record<string, 'Fire'|'Earth'|'Air'|'Water'> = {
				Aries: 'Fire', Leo: 'Fire', Sagittarius: 'Fire',
				Taurus: 'Earth', Virgo: 'Earth', Capricorn: 'Earth',
				Gemini: 'Air', Libra: 'Air', Aquarius: 'Air',
				Cancer: 'Water', Scorpio: 'Water', Pisces: 'Water'
			};
			for (let i = 0; i < nodes.length; i++) {
				const n: any = nodes[i];
				const color = n.color || '';
				let hue = 0;
				const m = /hsl\(([-\d.]+),/.exec(color);
				if (m) hue = parseFloat(m[1]);
				const z = toZodiac(hue);
				counts[z] = (counts[z] || 0) + 1;
				const elem = zodiacToElement[z];
				if (elem) elemCounts[elem] = (elemCounts[elem] || 0) + 1;
			}
			const topZ = Object.keys(counts).sort((a,b) => (counts[b]||0)-(counts[a]||0))[0];
			if (topZ) arr.push({ id: 'zodiac.dominant', name: 'Dominant Zodiac', category: 'zodiac', type: 'categorical', value: topZ });
			const topE = Object.keys(elemCounts).sort((a,b) => (elemCounts[b]||0)-(elemCounts[a]||0))[0];
			if (topE) arr.push({ id: 'element.dominant', name: 'Dominant Element', category: 'element', type: 'categorical', value: topE });
			const total = nodes.length;
			const elements = ['Fire','Earth','Air','Water'] as const;
			for (let e of elements) {
				const v = (elemCounts[e] || 0) / total;
				arr.push({ id: `element.mix.${e.toLowerCase()}`, name: `${e} Mix`, category: 'element_mix', type: 'numeric', value: v, min: 0, max: 1 });
			}
			return arr;
		});

		// Providers: conceptual abstraction (thought activity)
		this.paramRegistry.registerProvider(() => {
			const arr: any[] = [];
			const cas: any = (this.network as any).conceptualAbstraction;
			if (!cas) return arr;
			const space = cas.conceptualSpace || (cas.getConceptualSpace && cas.getConceptualSpace());
			const concepts: any = space?.concepts;
			if (concepts && typeof concepts.forEach === 'function') {
				let topId: string | null = null;
				let topEnergy = -Infinity;
				concepts.forEach((c: any, id: string) => {
					const energy = c?.energy ?? 0;
					arr.push({ id: `concept.energy.${id}`, name: `Concept Energy · ${id}`, category: 'thought', type: 'numeric', value: energy, min: 0, max: 1 });
					const power = c?.power ?? 0;
					arr.push({ id: `concept.power.${id}`, name: `Concept Power · ${id}`, category: 'thought', type: 'numeric', value: power, min: 0, max: 1 });
					const resonance = c?.resonance ?? 0;
					arr.push({ id: `concept.resonance.${id}`, name: `Concept Resonance · ${id}`, category: 'thought', type: 'numeric', value: resonance, min: 0, max: 1 });
					if (energy > topEnergy) { topEnergy = energy; topId = id; }
				});
				if (topId) {
					arr.push({ id: 'concept.dominant', name: 'Dominant Concept', category: 'thought', type: 'categorical', value: topId });
				}
			}
			return arr;
		});

		// Example extensibility: register a plugin for custom research logic
		this.network.registerPlugin((sim) => {
			// Example: log energy stats every 500 steps
			if (sim.step % 500 === 0) {
				const avgEnergy = sim.nodes.reduce((sum, n) => sum + n.energy, 0) / sim.nodes.length;
				console.log(`[App Research] Step ${sim.step}: Avg energy = ${avgEnergy.toFixed(2)}`);
			}
		});

		// Wire up restart button
		this.ui.restartBtn.onclick = () => {
			this.restartSimulation();
		};
		this.ui.playPauseBtn.onclick = () => {
			this.manuallyPaused = !this.manuallyPaused;
			this.ui.playPauseBtn.textContent = this.manuallyPaused ? 'Play Simulation' : 'Pause Simulation';
			this.ui.titleDiv.textContent = this.manuallyPaused ? 'Simulation Paused (Manual)' : 'Simulation Running';
			this.ui.titleDiv.style.color = '#fff';
		};

		this.loopActive = true;
		this.runMainLoop(); // <-- Start main simulation loop
	}

	cycleSimulationData() {
		const state = this.network.cycle(); // <-- use simulator's cycle method
		this.ui.render();
		// Update archetypes and draw pixel map
		if ((this.network as any).archetypeEngine?.updateFromNetwork) {
			(this.network as any).archetypeEngine.updateFromNetwork(this.network);
		}
		this.paramRegistry.update();
		this.pixelPanel.draw(this.paramRegistry);
		if (typeof this.network.runPlugins === 'function') {
			this.network.runPlugins();
		}
	}

	runMainLoop() {
		const SIM_STEPS_PER_FRAME = 1; // <-- Single step per frame for controlled pace
		let lastFrameTime = performance.now();
		const loop = () => {
			const frameStart = performance.now();
			if (!this.manuallyPaused) {
				try {
					for (let i = 0; i < SIM_STEPS_PER_FRAME; i++) {
						this.cycleSimulationData();
						// Slow growth is handled by forceGrowthEveryStep with timing
					}
					const failed = this.network.hasFailed();
					if (typeof failed === 'boolean' && failed) {
						this.ui.titleDiv.textContent = 'Simulation Failed';
						this.ui.titleDiv.style.color = '#c00';
						return;
					}
				} catch (err) {
					console.error('Simulation error:', err);
					this.ui.titleDiv.textContent = 'Simulation Error';
					this.ui.titleDiv.style.color = '#c00';
					return;
				}
			}
			const frameEnd = performance.now();
			lastFrameTime = frameEnd;
			requestAnimationFrame(loop);
		};
		loop();
	}

	init() {
		// Remove single simulation step, handled by main loop
		
		// Creature voice info is now displayed in the sidebar
	}

	showPsychicPotentialComparison(otherNetwork: NeuralNetworkSimulator) {
		const result = comparePsychicPotential(this.network, otherNetwork);
		this.ui.infoDiv.innerHTML += `<br><b>Psychic Potential Comparison:</b> Score: ${result.score.toFixed(2)}, Compatibility: ${(result.compatibility * 100).toFixed(1)}%<br>${result.details}`;
	}

	restartSimulation() {
		this.loopActive = false;
		// Stop all sounds before removing UI
		if (typeof this.ui.restartAllSounds === 'function') {
			this.ui.restartAllSounds();
		}
		// Remove old canvas and UI elements
		document.body.removeChild(this.ui.canvas);
		document.body.removeChild(this.ui.titleDiv);
		document.body.removeChild(this.ui.infoDiv);
		document.body.removeChild(this.ui.statsDiv);
		document.body.removeChild(this.ui.restartBtn);

		// Recreate network and UI
		this.network = new NeuralNetworkSimulator();
		this.ui = new UI(this.network);

		// Example extensibility: register plugin again after restart
		this.network.registerPlugin((sim) => {
			if (sim.step % 500 === 0) {
				const avgEnergy = sim.nodes.reduce((sum, n) => sum + n.energy, 0) / sim.nodes.length;
				console.log(`[App Research] Step ${sim.step}: Avg energy = ${avgEnergy.toFixed(2)}`);
			}
		});

		// Re-wire restart button
		this.ui.restartBtn.onclick = () => {
			this.ui.restartAllSounds();
			this.restartSimulation();
		};
		this.ui.playPauseBtn.onclick = () => {
			this.manuallyPaused = !this.manuallyPaused;
			this.ui.playPauseBtn.textContent = this.manuallyPaused ? 'Play Simulation' : 'Pause Simulation';
			this.ui.titleDiv.textContent = this.manuallyPaused ? 'Simulation Paused (Manual)' : 'Simulation Running';
			this.ui.titleDiv.style.color = '#fff';
		};

		this.loopActive = true;
		if (typeof this.runMainLoop === 'function') {
			this.runMainLoop(); // <-- Restart main simulation loop
		}
	}

	// Extensibility: Add more plugins, export/import, and research hooks as needed.
}

/*
Why is it not growing or simulating much?
- If SIM_STEPS_PER_FRAME is set to 1, growth will be very slow (one simulation step per animation frame).
- If NeuralNetworkSimulator.simulateEvolution() is only growing nodes every N steps or with high energy, growth will be slow.
- Other growth methods (e.g., forceGrowthEveryStep, evolutionaryGrowthAfterPulse) may also be limited by energy or step conditions.

To speed up growth, increase SIM_STEPS_PER_FRAME, or ensure simulateEvolution always grows at least one node per branch per step.
Check that cycleSimulationData() calls the simulator's cycle() method, which should call simulateEvolution and energizeNodes.
*/

// App logic is already modular and extensible.
// Comments clarify orchestration and extensibility.
// Comments clarify orchestration and extensibility.
